import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FieldValues, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import ReactCodeInput from "react-code-input";
import { ApolloError } from "@apollo/client";

import colors from "app/colors";
import Link from "modules/layouts/_shared/link";
import { IdentityType } from "gql/graphql";
import { ArrowLeftIcon } from "modules/icons";
import SyncoLogoIcon from "modules/icons/synco-logo-icon";
import { useAuth } from "modules/providers/auth-provider";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import { AUTH_REDIRECT_PATH } from "modules/utils/Constants";
import {
  getE164PhoneNumber,
  getMessageFromError,
} from "modules/utils/Functions";
import PhoneInput from "./phone-input";

enum State {
  choose = "CHOOSE",
  phone = "PHONE",
  email = "EMAIL",
  pin = "PIN",
}

const AuthPage = () => {
  const [state, setState] = useState(State.choose);
  const [identityType, setIdentityType] = useState<IdentityType>(
    IdentityType.Phone,
  );
  const { track } = useMixpanel();
  const {
    handleSubmit,
    formState: { errors },
    clearErrors,
    control,
    register,
    setError,
    setFocus,
    getValues,
  } = useForm({
    mode: "onSubmit",
  });

  const [token, setToken] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { authIdentify, authPin } = useAuth();

  const { push } = useRouter();

  useEffect(() => {
    clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const setStateOnClick = useCallback(
    (newState: State) => () => {
      setState(newState);
      switch (newState) {
        case State.phone:
          setIdentityType(IdentityType.Phone);
          break;
        case State.email:
          setIdentityType(IdentityType.Email);
          break;
        default:
          break;
      }
    },
    [],
  );

  useEffect(() => {
    switch (state) {
      case State.phone:
        setFocus("phone");
        break;
      case State.email:
        setFocus("email");
        break;
      default:
        break;
    }
  }, [setFocus, state]);

  const onPhoneOrEmailSubmit = useCallback(
    async ({ phone, email }: FieldValues) => {
      setIsSubmitting(true);
      try {
        const type = identityType === IdentityType.Phone ? "PHONE" : "EMAIL";
        const identity =
          identityType === IdentityType.Phone
            ? getE164PhoneNumber(phone)
            : email;
        const response = await authIdentify({
          identity,
          type,
        });
        const body = await response.json();
        if (response.status !== 200) {
          setError(type === "PHONE" ? "phone" : "email", body);
        }
        const { token } = body;
        setToken(token);
        setState(State.pin);
      } catch (error) {
        setError(email ? "email" : "phone", {
          type: "custom",
          message: (error as ApolloError).message,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [identityType, authIdentify, setError],
  );

  const onPinChange = useCallback(
    async (pin: string) => {
      clearErrors();

      if (pin.length !== 6) {
        return;
      }

      if (!token) {
        return;
      }

      try {
        setIsSubmitting(true);
        const response = await authPin({
          token,
          pin,
        });
        if (!response.ok) {
          const body = await response.text();
          throw new Error(body);
        }
        track(MIXPANEL_EVENT_TYPE.SIGN_IN, {
          "Sign In Method":
            identityType === IdentityType.Phone ? "Phone" : "Email",
        });
        push({
          pathname: AUTH_REDIRECT_PATH,
        });
      } catch (error) {
        setError("pin", {
          type: "custom",
          message: "Invalid PIN",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [clearErrors, token, authPin, track, identityType, push, setError],
  );

  const onResendPinClick = useCallback(() => {
    onPhoneOrEmailSubmit(getValues());
  }, [getValues, onPhoneOrEmailSubmit]);

  return (
    <Center h="100dvh" bg={colors.darkBlue}>
      <VStack
        w={80}
        p={6}
        bg="white"
        rounded="lg"
        spacing={4}
        alignItems="stretch"
      >
        <Box>
          {state !== State.choose && (
            <IconButton
              aria-label="Go back"
              variant="ghost"
              alignSelf="flex-start"
              position="absolute"
              icon={<ArrowLeftIcon boxSize={12} />}
              onClick={setStateOnClick(State.choose)}
            />
          )}
        </Box>
        <Center>
          <SyncoLogoIcon />
        </Center>
        {state === State.choose && (
          <VStack spacing={4} alignItems="stretch">
            <Button onClick={setStateOnClick(State.phone)}>
              Sign In with Phone Number
            </Button>
            <Button
              variant="outline"
              borderColor={colors.darkBlue}
              color={colors.darkBlue}
              onClick={setStateOnClick(State.email)}
            >
              Sign In with Email
            </Button>
          </VStack>
        )}
        {state === State.phone && (
          <form
            id="phone-submit-form"
            onSubmit={handleSubmit(onPhoneOrEmailSubmit)}
          >
            <PhoneInput name="phone" control={control} error={errors?.phone} />
            <Button type="submit" mt={4} w="100%" isLoading={isSubmitting}>
              Next
            </Button>
          </form>
        )}
        {state === State.email && (
          <form
            id="email-submit-form"
            onSubmit={handleSubmit(onPhoneOrEmailSubmit)}
          >
            <FormControl isInvalid={!!errors?.email}>
              <Input
                type="email"
                placeholder="Email"
                {...register("email", {
                  required: "Required",
                  pattern: {
                    value: /.+@.+\..+/,
                    message: "Invalid",
                  },
                })}
              />
              <FormErrorMessage justifyContent="flex-end">
                {getMessageFromError(errors?.email?.message)}
              </FormErrorMessage>
              <Button type="submit" mt={4} w="100%" isLoading={isSubmitting}>
                Next
              </Button>
            </FormControl>
          </form>
        )}
        {state === State.pin && (
          <FormControl isInvalid={!!errors?.pin}>
            <Text mb={4}>Enter the code that was sent to you</Text>
            <Box maxH={10}>
              <ReactCodeInput
                name="pin"
                type="text"
                fields={6}
                inputMode="numeric"
                autoFocus
                onChange={onPinChange}
                disabled={isSubmitting}
              />
            </Box>
            <FormErrorMessage justifyContent="flex-end">
              {getMessageFromError(errors?.pin?.message)}
            </FormErrorMessage>
            <Button
              variant="ghost"
              mt={4}
              onClick={onResendPinClick}
              isLoading={isSubmitting}
              h={10}
              w="100%"
            >
              Resend Code
            </Button>
          </FormControl>
        )}
        <Text pt={25} fontSize="xs" textAlign="center" color={colors.grayMain}>
          By signing in, you are agreeing to the{" "}
          <Link
            href="https://teamsynco.com/terms-of-service"
            isExternal
            color={colors.blue}
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            href="https://teamsynco.com/privacy"
            isExternal
            color={colors.blue}
          >
            Privacy Policy
          </Link>
        </Text>
      </VStack>
    </Center>
  );
};

AuthPage.prefersFullScreenLayout = true;

export default AuthPage;
